import { Link } from '@tanstack/react-router';
import { ChevronRight, Phone, UserRound } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatDate, formatKm } from '@/lib/format';
import { INCIDENT_KIND_BG, useDossier } from '@/api/hooks/dossier';
import { Badge } from '@/components/ui/Badge';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';

const STATUS_BG: Record<string, string> = {
  active: 'на работа',
  on_leave: 'в отпуск',
  inactive: 'неактивен',
};

/**
 * ШОФЬОРЪТ НА БУСА — телефонът, сроковете на книжката и медицинското, ПТП от
 * досието и последното вписано. Всичко останало е в досието му, на един клик.
 */
export function VanDriverCard({ driverId, role }: { driverId: string; role: string }) {
  const dossier = useDossier(driverId);
  if (dossier.isLoading) {
    return (
      <Card>
        <CardBody className="text-sm text-ink-dim">Зареждане на шофьора…</CardBody>
      </Card>
    );
  }
  if (!dossier.data) return null;
  const { driver, expiries, stats, incidents } = dossier.data;
  const last = incidents[0];

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            <UserRound className="size-4 text-ink-dim" />
            <Link to="/drivers/$driverId" params={{ driverId }} className="hover:text-accent">
              {driver.fullName}
            </Link>
            <span className="text-xs font-normal text-ink-dim">{role}</span>
            {driver.status !== 'active' ? <Badge tone="warn">{STATUS_BG[driver.status] ?? driver.status}</Badge> : null}
            {!driver.hasPhoneApp ? <Badge tone="neutral">без приложение</Badge> : null}
          </span>
        }
        actions={
          <Link to="/drivers/$driverId" params={{ driverId }} className="inline-flex items-center gap-0.5 text-xs text-accent hover:underline">
            Досие <ChevronRight className="size-3.5" />
          </Link>
        }
      />
      <CardBody className="space-y-2.5 text-sm">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {driver.phone ? (
            <a href={`tel:${driver.phone}`} className="inline-flex items-center gap-1.5 font-mono text-accent hover:underline">
              <Phone className="size-3.5" />
              {driver.phone}
            </a>
          ) : (
            <span className="text-ink-faint">без телефон</span>
          )}
          <span className="text-xs text-ink-dim">
            {stats.tripsLastYear} курса и {formatKm(stats.kmLastYear)} за 12 месеца
          </span>
          <span
            className={cn(
              'text-xs',
              stats.accidentsAtFault3y ? 'font-medium text-danger' : stats.accidents3y ? 'text-warn' : 'text-ink-dim',
            )}
          >
            {stats.accidents3y
              ? `ПТП за 3 години: ${stats.accidents3y}${stats.accidentsAtFault3y ? `, по негова вина ${stats.accidentsAtFault3y}` : ''}`
              : 'без ПТП за 3 години'}
          </span>
        </div>

        <div className="flex flex-wrap gap-1.5 text-xs">
          {expiries.map((e) => (
            <Badge key={e.key} tone={e.daysLeft === null ? 'neutral' : e.daysLeft < 0 ? 'danger' : e.daysLeft <= 45 ? 'warn' : 'ok'}>
              {e.label}: {e.date ? `${e.daysLeft !== null && e.daysLeft < 0 ? 'изтекло ' : 'до '}${formatDate(e.date)}` : 'не е въведено'}
            </Badge>
          ))}
        </div>

        {last ? (
          <p className="text-xs text-ink-dim">
            Последно в досието: <span className="font-mono">{formatDate(last.occurredOn)}</span> · {INCIDENT_KIND_BG[last.kind]} —{' '}
            <span className="text-ink">{last.description}</span>
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}
